// MSAL: the portal's single-tenant app registration, popup login and a
// localStorage cache shared with the portal (ADR 0008, ADR 0017).
import {
  EventType,
  PublicClientApplication,
  type AccountInfo,
  type Configuration,
  type EventMessage,
  type IPublicClientApplication,
  type PopupRequest,
  type SilentRequest
} from '@azure/msal-browser'
import { appBasePath } from './basePath'
import { restoreSession, setAccount, watchSharedSession, type SessionAccount } from './session'

/** Single-tenant authority, copied from the portal's src/utils/authConfig.ts. */
export const AUTHORITY = 'https://login.microsoftonline.com/cd4b8043-dff6-4d08-b7b9-80ff3387d8b2'

export function msalConfig(): Configuration {
  return {
    auth: {
      clientId: import.meta.env.APP_AZURE_AD_CLIENT_ID || '',
      authority: AUTHORITY,
      // `/auth-callback` when the base is `/`: the URI already registered (D5).
      redirectUri: `${window.location.origin}${appBasePath()}auth-callback`
    },
    // The portal also sets `storeAuthStateInCookie: false`: MSAL's default,
    // and deprecated since msal-browser 4.30, so it is left out.
    cache: { cacheLocation: 'localStorage' }
  }
}

export const loginRequest: PopupRequest = {
  scopes: ['User.Read'],
  prompt: 'select_account'
}

/**
 * Silent Azure token request. Unlike the portal (forceRefresh: true on every
 * checkAuth, i.e. a network round trip per navigation), we only need an Azure
 * token when the Oase token is within 10 minutes of expiry, and we let MSAL
 * serve it from its cache (it refreshes on its own when the cached one has
 * expired). `ensureFreshToken` retries once with `forceRefresh: true` when
 * the exchange says the Azure token is expired or MSAL needs interaction.
 */
export const tokenRequest: Omit<SilentRequest, 'account'> = {
  scopes: ['User.Read']
}

let instance: IPublicClientApplication | null = null

/** The MSAL instance, created on first use. */
export function getMsal(): IPublicClientApplication {
  instance ??= new PublicClientApplication(msalConfig())
  return instance
}

/** Replace the MSAL instance (tests inject a fake; `null` resets to lazy creation). */
export function setMsal(next: IPublicClientApplication | null) {
  instance = next
}

/** The active account, or the first cached one. */
export function getAccount(): AccountInfo | null {
  const msal = getMsal()
  return msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null
}

export function toSessionAccount(account: AccountInfo | null | undefined): SessionAccount | null {
  return account ? { id: account.localAccountId, name: account.username } : null
}

// MSAL 5 broadcasts these to other tabs itself (localStorage cache), which
// replaces MSAL 4's enableAccountStorageEvents and ACCOUNT_ADDED/REMOVED.
const ACCOUNT_EVENTS: readonly string[] = [
  EventType.LOGIN_SUCCESS,
  EventType.LOGOUT_SUCCESS,
  EventType.ACTIVE_ACCOUNT_CHANGED
]

/** True on the MSAL redirectUri, `/auth-callback` under the base path. */
export function isAuthCallback(): boolean {
  return window.location.pathname.replace(/\/$/, '') === `${appBasePath()}auth-callback`
}

/**
 * On `/auth-callback`: hand the response in the URL to the window that
 * started the popup or silent iframe (MSAL 5's redirect bridge). Resolves
 * false when there is no response to hand over; the app then boots as usual.
 */
export async function bridgeAuthResponse(): Promise<boolean> {
  try {
    const { broadcastResponseToMainFrame } = await import('@azure/msal-browser/redirect-bridge')
    await broadcastResponseToMainFrame()
    return true
  } catch (error) {
    console.error('Error handling auth callback:', error)
    return false
  }
}

/**
 * Boot: call once and await before rendering (ADR 0008). Mirrors the
 * portal's pages/index.tsx, then loads the shared session. Returns a
 * cleanup that stops watching the shared session.
 */
export async function initAuth(): Promise<() => void> {
  const msal = getMsal()
  await msal.initialize()
  try {
    const response = await msal.handleRedirectPromise()
    if (response?.account) msal.setActiveAccount(response.account)
  } catch (error) {
    console.error('Error handling redirect:', error)
  }
  if (!msal.getActiveAccount()) {
    const first = msal.getAllAccounts()[0]
    if (first) msal.setActiveAccount(first)
  }
  msal.addEventCallback((event: EventMessage) => {
    if (!ACCOUNT_EVENTS.includes(event.eventType)) return
    if (event.eventType === EventType.LOGIN_SUCCESS) {
      const payload = event.payload as { account?: AccountInfo } | null
      if (payload?.account) msal.setActiveAccount(payload.account)
    }
    setAccount(toSessionAccount(getAccount()))
  })
  setAccount(toSessionAccount(getAccount()))
  restoreSession()
  return watchSharedSession()
}
